import { Injectable } from '@angular/core';
import { Breadcrumb, NavItem } from '../models/navigation.model';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private navItems: NavItem[] = [
    {
      id: 'introduction',
      displayKey: 'NAV.INTRODUCTION',
      path: '/introduction',
      isCategory: false,
      children: [
        {
          id: 'smartDecision',
          displayKey: 'NAV.SMART_DECISION',
          path: '/introduction',
          isCategory: false,
          isSubItem: true,
          sectionId: 'welcome2'
        }
      ]
    },
    {
      id: 'benefits',
      displayKey: 'NAV.BENEFITS_OF_SMARTAPP',
      path: '/benefits',
      isCategory: false
    },
    {
      id: 'eligibility',
      displayKey: 'NAV.ELIGIBILITY_AND_USE',
      path: '/eligibility',
      isCategory: false
    },
    {
      id: 'gettingStarted',
      displayKey: 'NAV.GETTING_STARTED',
      path: '/gettingStarted',
      isCategory: false
    },
    {
      id: 'dashboard',
      displayKey: 'NAV.DASHBOARD_OVERVIEW',
      path: '/dashboard',
      isCategory: false
    },
    {
      id: 'createQuote',
      displayKey: 'NAV.CREATING_A_QUOTE',
      path: '/createQuote',
      isCategory: false
    },
    {
      id: 'completeApplication',
      displayKey: 'NAV.COMPLETE_APPLICATION',
      path: '/completeApplication',
      isCategory: true,
      isExpanded: false,
      enableCategoryRouting: true,
      children:[    
        {
          id: 'initialScreen',
          displayKey: 'NAV.INITIAL_SCREENS',
          path: '/initialScreen',
          isCategory: false,
          children: [
            { id: 'preScreen', displayKey: 'NAV.PRE_SCREEN', path: '/initialScreen', isCategory: false, isSubItem: true, sectionId: 'Pre Screen' },
            { id: 'advisor', displayKey: 'NAV.ADVISOR', path: '/initialScreen', isCategory: false, isSubItem: true, sectionId: 'Advisor' },
            { id: 'planSelection', displayKey: 'NAV.PLAN_SELECTION', path: '/initialScreen', isCategory: false, isSubItem: true, sectionId: 'Plan Selection' },
            { id: 'insured', displayKey: 'NAV.INSURED', path: '/initialScreen', isCategory: false, isSubItem: true, sectionId: 'Insured' },
          ]
        },
        {
          id: 'additionalDetails',
          displayKey: 'NAV.ADDITIONAL_DETAIL',
          path: '/additionalDetails',
          isCategory: false,
          children: [
            { id: 'owner', displayKey: 'NAV.OWNER', path: '/additionalDetails', isCategory: false, isSubItem: true, sectionId: 'Owner' },
            { id: 'coverageInfo', displayKey: 'NAV.COVERAGE_INFO', path: '/additionalDetails', isCategory: false, isSubItem: true, sectionId: 'Coverage Info' },
            { id: 'ridersBenefits', displayKey: 'NAV.RIDERS_BENEFITS', path: '/additionalDetails', isCategory: false, isSubItem: true, sectionId: 'Riders & Benefits' },
            { id: 'jointRiders', displayKey: 'NAV.JOINT_RIDERS', path: '/additionalDetails', isCategory: false, isSubItem: true, sectionId: 'Joint Riders' },
            { id: 'insuranceHistory', displayKey: 'NAV.INSURANCE_HISTORY', path: '/additionalDetails', isCategory: false, isSubItem: true, sectionId: 'Insurance History' },
          ]
        },
        {
          id: 'questionnaires',
          displayKey: 'NAV.QUESTIONNAIRES',
          path: '/questionnaires',
          isCategory: false,
          children: [
            { id: 'questionnaireOptions', displayKey: 'NAV.QUESTIONNAIRE_OPTIONS', path: '/questionnaires', isCategory: false, isSubItem: true, sectionId: 'Questionnaire Options' },
            { id: 'underwritingQuestions', displayKey: 'NAV.UNDERWRITING_QUESTIONS', path: '/questionnaires', isCategory: false, isSubItem: true, sectionId: 'Underwriting Questions' },
            { id: 'insurabilityQuestionnaire', displayKey: 'NAV.INSURABILITY_QUESTIONNAIRE', path: '/questionnaires', isCategory: false, isSubItem: true, sectionId: 'Insurability Questionnaire' },
          ]
        },
        {
          id: 'requirements',
          displayKey: 'NAV.REQUIREMENTS',
          path: '/requirements',
          isCategory: false
        },
        {
          id: 'finalScreen',
          displayKey: 'NAV.FINALSCREEN',
          path: '/finalScreen',
          isCategory: false,
          children: [
            { id: 'beneficiary', displayKey: 'NAV.BENEFICIARY', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'Beneficiary' },
            { id: 'beneficiaryCI', displayKey: 'NAV.BENEFICIARY_CI', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'Beneficiary for CI (on Critical Illness policies)' },
            { id: 'payor', displayKey: 'NAV.PAYOR', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'Payor' },
            { id: 'purpose', displayKey: 'NAV.PURPOSE', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'Purpose' },
            { id: 'paymentInfo', displayKey: 'NAV.PAYMENT_INFO', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'Payment Info' },
            { id: 'ulFundAllocation', displayKey: 'NAV.UL_FUND_ALLOCATION', path: '/finalScreen', isCategory: false, isSubItem: true, sectionId: 'UL Fund Allocation' },
          ]
        },
      ]
    },
    {
      id: 'reviewApplication',
      displayKey: 'NAV.REVIEW_APPLICATION',
      path: '/reviewApplication',
      isCategory: false
    },
    {
      id: 'eSignature',
      displayKey: 'NAV.ESIGNATURE_PROCESS',
      path: '/eSignature',
      isCategory: false,
      children: [
        { id: 'signatures', displayKey: 'NAV.SIGNATURES', path: '/eSignature', isCategory: false, isSubItem: true, sectionId: 'Signatures' },
        { id: 'aboutESignProcess', displayKey: 'NAV.ABOUT_ESIGN_PROCESS', path: '/eSignature', isCategory: false, isSubItem: true, sectionId: 'About the ESign Process' },
        { id: 'documents', displayKey: 'NAV.DOCUMENTS', path: '/eSignature', isCategory: false, isSubItem: true, sectionId: 'Documents' },
      ]
    },
    {
      id: 'manageESignature',
      displayKey: 'NAV.MANAGE_ESIGNATURE',
      path: '/manageESignature',
      isCategory: false
    },
    {
      id: 'submit',
      displayKey: 'NAV.SUBMIT_APPLICATION',
      path: '/submit',
      isCategory: false,
      children: [
        { id: 'advisorReport', displayKey: 'NAV.ADVISOR_REPORT', path: '/submit', isCategory: false, isSubItem: true, sectionId: 'Advisor Report' },
      ]
    },
    {
      id: 'needHelp',
      displayKey: 'NAV.NEED_HELP',
      path: '/needHelp',
      isCategory: false
    },
  ];

  private currentBreadcrumbsSubject = new BehaviorSubject<Breadcrumb[]>([]);
  private currentPathSubject = new BehaviorSubject<string>('');
  private currentSectionIdSubject = new BehaviorSubject<string | null>(null);

  constructor(private router: Router) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentPathSubject.next(event.urlAfterRedirects);
      this.updateBreadcrumbs(event.urlAfterRedirects);
    });
  }

  getNavItems(): NavItem[] {
    return this.navItems;
  }

  getCurrentPath(): Observable<string> {
    return this.currentPathSubject.asObservable();
  }

  getCurrentBreadcrumbs(): Observable<Breadcrumb[]> {
    return this.currentBreadcrumbsSubject.asObservable();
  }

  getCurrentSectionId(): Observable<string | null> {
    return this.currentSectionIdSubject.asObservable();
  }

  setCurrentSectionId(sectionId: string): void {
    this.currentSectionIdSubject.next(sectionId);
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setCurrentSectionId(sectionId);
    }
  }

  toggleExpandItem(item: NavItem): void {
    item.isExpanded = !item.isExpanded;
  }

  // Find the first non-category child of a category
  getFirstChildPath(categoryItem: NavItem): string | null {
    if (categoryItem.children && categoryItem.children.length > 0) {
      // If the first child is also a category, recursively find its first child
      if (categoryItem.children[0].isCategory) {
        return this.getFirstChildPath(categoryItem.children[0]);
      }
      return categoryItem.children[0].path;
    }
    return null;
  }

  private updateBreadcrumbs(path: string): void {
    const breadcrumbs: Breadcrumb[] = [];
    
    // Split the path and build breadcrumbs
    const segments = path.split('/').filter(segment => segment);
    
    let currentPath = '';
    segments.forEach(segment => {
      currentPath += `/${segment}`;
      
      const navItem = this.findNavItemByPath(currentPath);
      if (navItem) {
        breadcrumbs.push({
          displayKey: navItem.displayKey,
          path: navItem.isCategory ? '' : navItem.path // Empty path for categories to prevent navigation
        });
      }
    });
    
    this.currentBreadcrumbsSubject.next(breadcrumbs);
  }

  private findNavItemByPath(path: string): NavItem | null {
    // Helper function to recursively search for a nav item by its path
    const findItem = (items: NavItem[], targetPath: string): NavItem | null => {
      for (const item of items) {
        if (item.path === targetPath) {
          return item;
        }
        if (item.children) {
          const found = findItem(item.children, targetPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };
    
    return findItem(this.navItems, path);
  }

  // Get next and previous pages for navigation (skipping categories)
  getNextPage(currentPath: string): NavItem | null {
    // Flatten navigation structure to find next page
    const flatNavigation = this.flattenNavigation(this.navItems).filter(item => this.shouldNavigate(item));
    const currentIndex = flatNavigation.findIndex(item => item.path === currentPath);
    
    if (currentIndex !== -1 && currentIndex < flatNavigation.length - 1) {
      return flatNavigation[currentIndex + 1];
    }
    return null;
  }

  getPreviousPage(currentPath: string): NavItem | null {
    // Flatten navigation structure to find previous page
    const flatNavigation = this.flattenNavigation(this.navItems).filter(item => this.shouldNavigate(item));
    const currentIndex = flatNavigation.findIndex(item => item.path === currentPath);
    
    if (currentIndex > 0) {
      return flatNavigation[currentIndex - 1];
    }
    return null;
  }

  private flattenNavigation(items: NavItem[]): NavItem[] {
    let flatItems: NavItem[] = [];
    
    for (const item of items) {
      flatItems.push(item);
      if (item.children && item.children.length > 0) {
        flatItems = flatItems.concat(this.flattenNavigation(item.children));
      }
    }
    
    return flatItems;
  }

  private shouldNavigate(item: NavItem): boolean {
    if (item.isSubItem) return false;
    return (!item.isCategory) || (!!item.isCategory && !!item.enableCategoryRouting)
  }
}